import { useState, useEffect } from 'react';
import { differenceInDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Pause, Clock, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { SuspensionInfo } from '@/types/calendar';

// Libellés des raisons
const RAISONS: Record<string, string> = {
  intemperies: 'Intempéries',
  attente_materiaux: 'Attente matériaux',
  probleme_client: 'Problème client',
  autre: 'Autre',
};

interface SuspensionBadgeProps {
  chantierId: string;
  etatChantier: string;
  onClick?: () => void;
  className?: string;
  compact?: boolean;
}

export function SuspensionBadge({
  chantierId,
  etatChantier,
  onClick,
  className,
  compact = false,
}: SuspensionBadgeProps) {
  const [suspensionInfo, setSuspensionInfo] = useState<SuspensionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Charger les informations de suspension
  useEffect(() => {
    const loadSuspension = async () => {
      // Seulement si le chantier est suspendu ou pour vérifier une suspension partielle
      if (etatChantier !== 'suspendu' && etatChantier !== 'en_cours') {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('suspensions_chantiers')
          .select('*')
          .eq('chantier_id', chantierId)
          .is('date_reprise', null)
          .order('date_suspension', { ascending: false })
          .limit(1)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('[SuspensionBadge] Erreur:', error);
        }

        setSuspensionInfo(data as SuspensionInfo | null);
      } catch (error) {
        console.error('[SuspensionBadge] Erreur:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSuspension();
  }, [chantierId, etatChantier]);

  // Ne rien afficher si pas de suspension
  if (loading || !suspensionInfo) {
    // Si c'est un chantier suspendu mais on n'a pas encore les infos, afficher un badge minimal
    if (etatChantier === 'suspendu') {
      return (
        <div
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium",
            "bg-amber-500/20 text-amber-500 border border-amber-500/30",
            onClick && "cursor-pointer hover:bg-amber-500/30",
            className
          )}
          onClick={onClick}
        >
          <Pause className="w-3 h-3" />
          {!compact && <span>Suspendu</span>}
        </div>
      );
    }
    return null;
  }

  const joursSuspension = differenceInDays(new Date(), new Date(suspensionInfo.date_suspension));
  const raisonLabel = RAISONS[suspensionInfo.raison] || suspensionInfo.raison;
  const isPartielle = suspensionInfo.type_suspension === 'partielle';
  const nbMembresSuspendus = suspensionInfo.affectations_suspendues?.length || 0;

  // Couleur selon type
  const badgeColor = isPartielle
    ? "bg-yellow-500/20 text-yellow-500 border-yellow-500/30"
    : "bg-amber-500/20 text-amber-500 border-amber-500/30";

  const hoverColor = isPartielle
    ? "hover:bg-yellow-500/30"
    : "hover:bg-amber-500/30";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border",
              badgeColor,
              onClick && `cursor-pointer ${hoverColor}`,
              className
            )}
            onClick={onClick}
          >
            <Pause className="w-3 h-3" />
            {!compact && (
              <span>
                {isPartielle ? 'Part. suspendu' : 'Suspendu'}
                {' '}
                <span className="opacity-75">({joursSuspension}j)</span>
              </span>
            )}
            {compact && <span>{joursSuspension}j</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="space-y-2">
            {/* Titre */}
            <div className="font-medium flex items-center gap-2">
              <Pause className="w-4 h-4" />
              {isPartielle ? 'Suspension partielle' : 'Suspension totale'}
            </div>

            {/* Détails */}
            <div className="text-sm space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Depuis</span>
                <span>{format(new Date(suspensionInfo.date_suspension), 'dd MMM yyyy', { locale: fr })}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Durée</span>
                <span>{joursSuspension} jour{joursSuspension > 1 ? 's' : ''}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Raison</span>
                <span>{raisonLabel}</span>
              </div>
              {suspensionInfo.raison_detail && (
                <div className="text-xs text-muted-foreground mt-1 italic">
                  {suspensionInfo.raison_detail}
                </div>
              )}
            </div>

            {/* Membres suspendus */}
            {nbMembresSuspendus > 0 && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-3 h-3" />
                  <span>{nbMembresSuspendus} membre{nbMembresSuspendus > 1 ? 's' : ''} suspendu{nbMembresSuspendus > 1 ? 's' : ''}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {suspensionInfo.affectations_suspendues?.slice(0, 3).map((aff, i) => (
                    <span key={i} className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      {aff.membre_prenom}
                    </span>
                  ))}
                  {nbMembresSuspendus > 3 && (
                    <span className="text-xs text-muted-foreground">
                      +{nbMembresSuspendus - 3}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Date reprise estimée */}
            {suspensionInfo.date_reprise_estimee && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-3 h-3" />
                  <span>
                    Reprise estimée : {format(new Date(suspensionInfo.date_reprise_estimee), 'dd MMM yyyy', { locale: fr })}
                  </span>
                </div>
              </div>
            )}

            {/* Call to action */}
            {onClick && (
              <div className="pt-2 text-xs text-primary text-center">
                Cliquez pour reprendre le chantier
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default SuspensionBadge;
